import type * as THREE from 'three';
import { SIM, TELEMETRY } from '../config/tuning';

/**
 * The flight recorder (SPEC §3.15).
 *
 * Aircraft state at 20 Hz plus discrete events, into a ring buffer. SPEC is explicit about the
 * representation: *"At 20 Hz and 12 minutes this is roughly 14 400 samples: keep it compact
 * (typed arrays), not an array of objects."* Fourteen thousand small objects would be fourteen
 * thousand allocations and a garbage collection pause in the middle of an engagement — exactly
 * where the game can least afford one.
 *
 * So samples live in parallel `Float32Array`s, one per field, indexed by sample number. The cost
 * is that reading a sample means an index rather than a reference; the benefit is that a whole
 * twelve-minute flight is about half a megabyte, allocated once at construction and never again.
 *
 * The recorder is also the reason the debrief can explain *why* a missile hit (SPEC §3.6.5) — it
 * is the only thing that remembers what the player actually did.
 */

/** Discrete events worth a timeline entry. */
export const TelemetryEvent = {
  Launch: 'launch',
  LockChanged: 'lock',
  Chaff: 'chaff',
  Flare: 'flare',
  Detonation: 'detonation',
  Impact: 'impact',
  Radio: 'radio',
  ObjectiveChanged: 'objective',
} as const;
export type TelemetryEventId = (typeof TelemetryEvent)[keyof typeof TelemetryEvent];

export interface TelemetryMark {
  readonly timeS: number;
  readonly kind: TelemetryEventId;
  /** Free-text detail: a designation, a state, a grade. */
  readonly detail: string;
  /** Optional scalar the debrief can use — miss distance, damage, whatever the event carries. */
  readonly value: number;
}

/** One decoded sample. Materialised on demand; the storage is columnar. */
export interface FlightSample {
  timeS: number;
  x: number;
  y: number;
  z: number;
  headingRad: number;
  pitchRad: number;
  bankRad: number;
  airspeedMs: number;
  altitudeAglM: number;
  loadFactor: number;
  integrity: number;
  throttle: number;
}

export class FlightRecorder {
  readonly capacity: number;

  private readonly timeS: Float32Array;
  private readonly x: Float32Array;
  private readonly y: Float32Array;
  private readonly z: Float32Array;
  private readonly heading: Float32Array;
  private readonly pitch: Float32Array;
  private readonly bank: Float32Array;
  private readonly airspeed: Float32Array;
  private readonly altitudeAgl: Float32Array;
  private readonly loadFactor: Float32Array;
  private readonly integrity: Float32Array;
  private readonly throttle: Float32Array;

  private countValue = 0;
  private accumulatorS = 0;

  readonly marks: TelemetryMark[] = [];

  constructor(capacity: number = TELEMETRY.capacity) {
    this.capacity = capacity;
    this.timeS = new Float32Array(capacity);
    this.x = new Float32Array(capacity);
    this.y = new Float32Array(capacity);
    this.z = new Float32Array(capacity);
    this.heading = new Float32Array(capacity);
    this.pitch = new Float32Array(capacity);
    this.bank = new Float32Array(capacity);
    this.airspeed = new Float32Array(capacity);
    this.altitudeAgl = new Float32Array(capacity);
    this.loadFactor = new Float32Array(capacity);
    this.integrity = new Float32Array(capacity);
    this.throttle = new Float32Array(capacity);
  }

  get count(): number {
    return this.countValue;
  }

  get durationS(): number {
    return this.countValue === 0 ? 0 : this.timeS[this.countValue - 1];
  }

  reset(): void {
    this.countValue = 0;
    this.accumulatorS = 0;
    this.marks.length = 0;
  }

  /**
   * Sample the aircraft, decimated to `SIM.TELEMETRY_HZ`.
   *
   * Called from the fixed tick so the sample rate is exact and reproducible. Sampling on the
   * render frame instead would make a recording depend on the display rate, and a replay that
   * differs between a 60 Hz and a 144 Hz machine is not a replay.
   */
  sample(
    dtS: number,
    timeS: number,
    state: {
      position: THREE.Vector3;
      headingRad: number;
      pitchRad: number;
      bankRad: number;
      airspeedMs: number;
      loadFactor: number;
      integrity: number;
      throttleActual: number;
    },
    altitudeAglM: number,
  ): void {
    this.accumulatorS += dtS;
    const interval = 1 / SIM.TELEMETRY_HZ;
    if (this.accumulatorS < interval) return;
    this.accumulatorS -= interval;

    // A full buffer stops recording rather than wrapping. A ring that overwrites its own start
    // would silently discard the ingress — which is the half of the flight the debrief is about.
    if (this.countValue >= this.capacity) return;

    const i = this.countValue;
    this.timeS[i] = timeS;
    this.x[i] = state.position.x;
    this.y[i] = state.position.y;
    this.z[i] = state.position.z;
    this.heading[i] = state.headingRad;
    this.pitch[i] = state.pitchRad;
    this.bank[i] = state.bankRad;
    this.airspeed[i] = state.airspeedMs;
    this.altitudeAgl[i] = altitudeAglM;
    this.loadFactor[i] = state.loadFactor;
    this.integrity[i] = state.integrity;
    this.throttle[i] = state.throttleActual;
    this.countValue += 1;
  }

  /** Record a discrete event. Cheap; these are rare compared to samples. */
  mark(timeS: number, kind: TelemetryEventId, detail: string, value = 0): void {
    if (this.marks.length >= TELEMETRY.maxMarks) return;
    this.marks.push({ timeS, kind, detail, value });
  }

  /** Decode one sample into a caller-supplied object. Never allocates. */
  read(index: number, into: FlightSample): FlightSample {
    const i = Math.max(0, Math.min(this.countValue - 1, index));
    into.timeS = this.timeS[i];
    into.x = this.x[i];
    into.y = this.y[i];
    into.z = this.z[i];
    into.headingRad = this.heading[i];
    into.pitchRad = this.pitch[i];
    into.bankRad = this.bank[i];
    into.airspeedMs = this.airspeed[i];
    into.altitudeAglM = this.altitudeAgl[i];
    into.loadFactor = this.loadFactor[i];
    into.integrity = this.integrity[i];
    into.throttle = this.throttle[i];
    return into;
  }

  /** The sample at or just before a time, for the replay scrubber. */
  indexAtTime(timeS: number): number {
    if (this.countValue === 0) return 0;
    // Binary search: a linear scan is fine at 14 400 samples but this is called per scrub frame.
    let low = 0;
    let high = this.countValue - 1;
    while (low < high) {
      const mid = (low + high + 1) >> 1;
      if (this.timeS[mid] <= timeS) low = mid;
      else high = mid - 1;
    }
    return low;
  }

  /** Mean of a column over the whole recording — the debrief's ingress-altitude term. */
  meanAltitudeAglM(): number {
    if (this.countValue === 0) return 0;
    let total = 0;
    for (let i = 0; i < this.countValue; i += 1) total += this.altitudeAgl[i];
    return total / this.countValue;
  }

  /** Lowest and highest altitude flown, metres. */
  altitudeRangeM(): { minM: number; maxM: number } {
    if (this.countValue === 0) return { minM: 0, maxM: 0 };
    let minM = Number.POSITIVE_INFINITY;
    let maxM = Number.NEGATIVE_INFINITY;
    for (let i = 0; i < this.countValue; i += 1) {
      if (this.altitudeAgl[i] < minM) minM = this.altitudeAgl[i];
      if (this.altitudeAgl[i] > maxM) maxM = this.altitudeAgl[i];
    }
    return { minM, maxM };
  }

  /** Peak load factor pulled, for the debrief's manoeuvre analysis. */
  peakLoadFactor(): number {
    let peak = 0;
    for (let i = 0; i < this.countValue; i += 1) {
      const n = Math.abs(this.loadFactor[i]);
      if (n > peak) peak = n;
    }
    return peak;
  }

  /**
   * Ground track as a flat array of `x, z` pairs, for the debrief map.
   *
   * `stride` decimates: a 14 400-sample track drawn point-for-point on a 600 px map is 24 samples
   * per pixel, so most of the work is invisible.
   */
  groundTrack(stride = TELEMETRY.trackStride): Float32Array {
    const points = Math.max(1, Math.ceil(this.countValue / stride));
    const out = new Float32Array(points * 2);
    let w = 0;
    for (let i = 0; i < this.countValue; i += stride) {
      out[w] = this.x[i];
      out[w + 1] = this.z[i];
      w += 2;
    }
    return out.subarray(0, w);
  }

  /** Export as JSON (SPEC §3.15). Verbose on purpose — this is for humans and bug reports. */
  toJSON(): string {
    const samples: FlightSample[] = [];
    const scratch: FlightSample = {
      timeS: 0,
      x: 0,
      y: 0,
      z: 0,
      headingRad: 0,
      pitchRad: 0,
      bankRad: 0,
      airspeedMs: 0,
      altitudeAglM: 0,
      loadFactor: 0,
      integrity: 0,
      throttle: 0,
    };
    for (let i = 0; i < this.countValue; i += 1) samples.push({ ...this.read(i, scratch) });
    return JSON.stringify({ version: 1, samples, marks: this.marks });
  }
}
